import type { TileGetter } from "@/cache/PVSTileService/TileGetters/TileGetter";
import { hashPixelGridRequest } from "@/cache/PVSTileService/TileGetters/tile-getter-utils/hashApiRequest";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import type { BinaryTile } from "@/cache/SpatioTemporalTileCache/BinaryTileCache";
import type { TileDataDescription } from "@/cache/SpatioTemporalTileCache/TileDataDescription";
import { prepareBinaryCrop } from "@/cache/SpatioTemporalTileCache/binaryCropUtils";
import { recordLayerFetchMs } from "@/otel/metrics";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import {
  type CoordinateSystem,
  type GridRequest,
  GridSamplingStrategyKind,
  MeteomaticsApiUrl,
  type ParameterUnit,
} from "@mm/api.meteomatics.com";
import Logger from "@mm/logging";
import type { BinaryAnomalyDefinition } from "./BinaryAnomalyDefinitions";
import { BINARY_ANOMALY_GRID_SIZE } from "./BinaryAnomalyWorkerContract";

const logger = Logger.fromFilename(__filename);
let anomalyRequestSequence = 0;

function requestPairId(
  primaryRequest: GridRequest<CoordinateSystem.EPSG3857>,
  referenceRequest: GridRequest<CoordinateSystem.EPSG3857>,
): string {
  const input = `${hashPixelGridRequest(primaryRequest)}|${hashPixelGridRequest(referenceRequest)}`;
  let hash = 2166136261;
  for (let index = 0; index < input.length; index++) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export type BinaryAnomalyRequestParams = {
  model: string;
  api_url_suffix?: string;
};

export type BinaryAnomalyTileApiRequest = {
  primaryRequest: GridRequest<CoordinateSystem.EPSG3857>;
  referenceRequest: GridRequest<CoordinateSystem.EPSG3857>;
};

export type BinaryAnomalyRequestObserver = {
  onRequestStarted?: () => void;
  onRequestSucceeded?: () => void;
  onRequestFailed?: (error: unknown) => void;
};

function getActualBinaryUrl(request: GridRequest<CoordinateSystem.EPSG3857>): string | undefined {
  const cropPlan = prepareBinaryCrop(request);
  if (cropPlan.kind === "empty") {
    return undefined;
  }
  return MeteomaticsApiUrl.forGrid({ format: "bin" }, cropPlan.request).toString();
}

export function createBinaryAnomalyTileGetter(
  definition: BinaryAnomalyDefinition,
  emptyTile: BinaryTile,
  failureTile: BinaryTile,
  observer?: BinaryAnomalyRequestObserver,
): TileGetter<CoordinateSystem.EPSG3857, BinaryTile, BinaryAnomalyRequestParams, BinaryAnomalyTileApiRequest> {
  return {
    createEmptyTile: () => emptyTile,
    createFailureTile: () => failureTile,
    createTileApiRequest: (tileDesc: TileDataDescription<BinaryAnomalyRequestParams>) => {
      const commonRequest = {
        boundingBoxLimit: getModelBoundingBox(tileDesc.requestParams.model),
        model: tileDesc.requestParams.model,
        datetime: tileDesc.datetime,
        area: tileDesc.geometry.toEpsg3857(),
        sampling: { kind: GridSamplingStrategyKind.Area } as const,
        width: BINARY_ANOMALY_GRID_SIZE,
        height: BINARY_ANOMALY_GRID_SIZE,
        calibrated: false,
        vertical_interpolation: "none" as const,
        apiUrlSuffix: tileDesc.requestParams.api_url_suffix,
        decluttered: true,
      };
      return {
        primaryRequest: {
          ...commonRequest,
          parameters: [definition.primaryParameter as ParameterUnit],
        },
        referenceRequest: {
          ...commonRequest,
          parameters: [definition.referenceParameter as ParameterUnit],
          ensSelect: undefined,
          initDate: undefined,
        },
      };
    },
    requestTile: async ({ primaryRequest, referenceRequest }) => {
      observer?.onRequestStarted?.();
      const fetchStarted = performance.now();
      const pairId = requestPairId(primaryRequest, referenceRequest);
      const logLifecycle = anomalyRequestSequence++ % 32 === 0;
      if (logLifecycle) {
        logger.log(`Anomaly pair ${definition.id} started (${pairId}).`);
      }
      try {
        const tile = await apiThreadPool.getBinaryDifferenceTile(primaryRequest, referenceRequest, definition.id);
        const durationMs = performance.now() - fetchStarted;
        recordLayerFetchMs({
          durationMs,
          source: "binary_anomaly_tile",
        });
        if (logLifecycle) {
          logger.log(`Anomaly pair ${definition.id} succeeded (${pairId}) in ${Math.round(durationMs)} ms.`);
        }
        observer?.onRequestSucceeded?.();
        return tile;
      } catch (error) {
        const stage =
          error && typeof error === "object" && "binaryDifferenceStage" in error
            ? String(error.binaryDifferenceStage)
            : "unknown";
        logger.warn(`Anomaly pair ${definition.id} failed (${pairId}) at ${stage}.`);
        observer?.onRequestFailed?.(error);
        throw error;
      }
    },
    getTileCacheId: ({ primaryRequest, referenceRequest }) =>
      [
        definition.id,
        hashPixelGridRequest(primaryRequest),
        hashPixelGridRequest(referenceRequest),
        "output=64x64",
        "no-data=-999",
        "interpolation=nearest",
        `style=${definition.style.id}`,
      ].join("|"),
    getAbortUrls: ({ primaryRequest, referenceRequest }) =>
      [getActualBinaryUrl(primaryRequest), getActualBinaryUrl(referenceRequest)].filter(
        (url): url is string => url !== undefined,
      ),
  };
}
